import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { CateringVendorService } from '../../../../core/services/catering-vendor.service';
import { CateringCartService } from '../../../../core/services/catering-cart.service';
import { BookMeetingService } from '../../../../core/services/book-meeting.service';
import { DietaryPreferencesService } from '../../../../core/services/dietary-preferences.service';
import { BookMeetingView, isVendorType, VendorType } from '../../../../core/constants/enums';
import { VendorCardComponent } from '../vendor-card/vendor-card.component';
import { DietaryPreferencesSetupDialogComponent } from '../dietary-preferences-setup-dialog/dietary-preferences-setup-dialog.component';
import { CateringVendor } from '../../../../core/models/catering.model';
import { PaperButtonComponent } from '@ricoh-paper/angular';

/**
 * VendorListComponent — browses available caterers for the active meeting room.
 *
 * Renders a grouped list (Featured / Open now / Others) with a two-tier filter
 * bar: primary toggles (Open now, Featured) on top, dynamic tag chips below.
 * While vendors load, shimmer skeletons are shown in place of real cards.
 */
@Component({
  selector: 'im-catering-vendor-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [VendorCardComponent, DietaryPreferencesSetupDialogComponent, PaperButtonComponent, TranslocoPipe],
  styleUrl: './vendor-list.component.scss',
  template: `
    <!-- Dietary preferences setup dialog -->
    @if (showSetupDialog()) {
      <ct-dietary-preferences-setup
        (preferencesSaved)="onPreferencesSaved($event)"
        (cancelled)="onSetupCancelled()" />
    }

    <div class="vl">
      <!-- Filter bar: primary row + dynamic tags row -->
      <div class="vl__filters">
        <div class="vl__filter-row vl__filter-row--primary">
          <paper-button
            class="vl__filter-chip vl__filter-chip--primary"
            shape="pill"
            size="xs"
            [intent]="vendorService.openOnlyFilter() ? 'primary' : 'secondary'"
            [ariaPressed]="vendorService.openOnlyFilter()"
            (click)="vendorService.toggleOpenOnly()"
          >
            {{ 'catering.customer.vendor_list.filter.open_now' | transloco }}
          </paper-button>
          <paper-button
            class="vl__filter-chip vl__filter-chip--primary"
            shape="pill"
            size="xs"
            [intent]="vendorService.featuredOnlyFilter() ? 'primary' : 'secondary'"
            [ariaPressed]="vendorService.featuredOnlyFilter()"
            (click)="vendorService.toggleFeaturedOnly()"
          >
            {{ 'catering.customer.vendor_list.filter.featured' | transloco }}
          </paper-button>
        </div>

        @if (vendorService.availableTags().length > 0) {
          <div class="vl__filter-row">
            <span class="vl__filter-label">{{ 'catering.customer.vendor_list.filter.tags' | transloco }}</span>
            @for (tag of vendorService.availableTags(); track tag) {
              <paper-button
                class="vl__filter-chip"
                shape="pill"
                size="xs"
                [intent]="vendorService.tagFilter().includes(tag) ? 'primary' : 'secondary'"
                [ariaPressed]="vendorService.tagFilter().includes(tag)"
                (click)="toggleTag(tag)"
              >
                {{ tag }}
              </paper-button>
            }
          </div>
        }
      </div>

      <!-- Content: loading | empty | grouped grid -->
      @if (vendorService.loading() && vendorService.vendors().length === 0) {
        <div class="vl__skeleton-grid" aria-busy="true" [attr.aria-label]="'catering.customer.vendor_list.loading' | transloco">
          @for (_ of [1, 2, 3, 4]; track $index) {
            <div class="vl__skeleton" aria-hidden="true">
              <div class="vl__skeleton-hero"></div>
              <div class="vl__skeleton-body">
                <div class="vl__skeleton-bone vl__skeleton-bone--md"></div>
                <div class="vl__skeleton-bone vl__skeleton-bone--sm"></div>
              </div>
            </div>
          }
        </div>
      } @else if (vendorService.filteredVendors().length === 0) {
        <div class="vl__empty">
          <h4>{{ 'catering.customer.vendor_list.empty.title' | transloco }}</h4>
          <p>{{ 'catering.customer.vendor_list.empty.body' | transloco }}</p>
          @if (hasActiveFilters()) {
            <paper-button class="vl__empty-reset" intent="secondary" size="sm" shape="pill" (click)="clearFilters()">
              {{ 'catering.customer.vendor_list.empty.reset' | transloco }}
            </paper-button>
          }
        </div>
      } @else {
        @let groups = vendorService.groupedVendors();

        @if (groups.featured.length > 0) {
          <section class="vl__group">
            <div class="vl__group-header vl__group-header--featured">
              <span class="vl__group-icon" aria-hidden="true">&#9733;</span>
              <h4>{{ 'catering.customer.vendor_list.group.featured' | transloco }}</h4>
              <span class="vl__group-count">{{ groups.featured.length }}</span>
            </div>
            <div class="vl__grid">
              @for (vendor of groups.featured; track vendor._id) {
                <ct-vendor-card [vendor]="vendor" (selected)="onVendorSelected($event)" />
              }
            </div>
          </section>
        }

        @if (groups.openNow.length > 0) {
          <section class="vl__group">
            <div class="vl__group-header vl__group-header--open">
              <span class="vl__group-icon" aria-hidden="true">&#9679;</span>
              <h4>{{ 'catering.customer.vendor_list.group.open_now' | transloco }}</h4>
              <span class="vl__group-count">{{ groups.openNow.length }}</span>
            </div>
            <div class="vl__grid">
              @for (vendor of groups.openNow; track vendor._id) {
                <ct-vendor-card [vendor]="vendor" (selected)="onVendorSelected($event)" />
              }
            </div>
          </section>
        }

        @if (groups.others.length > 0) {
          <section class="vl__group">
            <div class="vl__group-header">
              <h4>{{ 'catering.customer.vendor_list.group.all_caterers' | transloco }}</h4>
              <span class="vl__group-count">{{ groups.others.length }}</span>
            </div>
            <div class="vl__grid">
              @for (vendor of groups.others; track vendor._id) {
                <ct-vendor-card [vendor]="vendor" (selected)="onVendorSelected($event)" />
              }
            </div>
          </section>
        }
      }
    </div>
  `,
})
export class VendorListComponent {
  protected readonly vendorService = inject(CateringVendorService);
  private readonly bookMeetingService = inject(BookMeetingService);
  private readonly cartService = inject(CateringCartService);
  private readonly dietaryPrefsService = inject(DietaryPreferencesService);

  /** Load vendors when the active room for catering changes */
  private readonly _loadEffect = effect(() => {
    const room = this.bookMeetingService.activeRoomForCatering();
    if (room?.locationId) {
      this.vendorService.loadVendors(room.locationId);
    }
  });

  // Dietary preferences setup dialog state
  protected readonly showSetupDialog = signal(false);
  private _pendingVendor: CateringVendor | null = null;

  protected toggleTag(tag: string): void {
    const current = this.vendorService.tagFilter();
    const next = current.includes(tag)
      ? current.filter(t => t !== tag)
      : [...current, tag];
    this.vendorService.setTagFilter(next);
  }

  protected hasActiveFilters(): boolean {
    return (
      this.vendorService.tagFilter().length > 0 ||
      this.vendorService.openOnlyFilter() ||
      this.vendorService.featuredOnlyFilter()
    );
  }

  protected clearFilters(): void {
    this.vendorService.setTagFilter([]);
    if (this.vendorService.openOnlyFilter()) this.vendorService.toggleOpenOnly();
    if (this.vendorService.featuredOnlyFilter()) this.vendorService.toggleFeaturedOnly();
  }

  protected onVendorSelected(vendor: CateringVendor): void {
    // Check if dietary preferences setup is needed (first-time user)
    if (!this.dietaryPrefsService.setupComplete()) {
      // Store vendor and show setup dialog
      this._pendingVendor = vendor;
      this.showSetupDialog.set(true);
      return;
    }

    // Setup already complete, proceed directly
    this.proceedWithVendor(vendor);
  }

  protected onPreferencesSaved(event: { dietaryTags: string[]; allergens: string[] }): void {
    // Save preferences and mark setup as complete
    this.dietaryPrefsService.completeSetup(event.dietaryTags, event.allergens);
    this.showSetupDialog.set(false);

    // Proceed with pending vendor
    if (this._pendingVendor) {
      this.proceedWithVendor(this._pendingVendor);
      this._pendingVendor = null;
    }
  }

  protected onSetupCancelled(): void {
    // User skipped setup - mark as complete (so we don't ask again)
    this.dietaryPrefsService.skipSetup();
    this.showSetupDialog.set(false);

    // Proceed with pending vendor
    if (this._pendingVendor) {
      this.proceedWithVendor(this._pendingVendor);
      this._pendingVendor = null;
    }
  }

  private proceedWithVendor(vendor: CateringVendor): void {
    this.vendorService.selectVendor(vendor._id);

    // Init cart with the meeting start time as default delivery time
    const meetingStart = this.bookMeetingService.startTime();
    this.cartService.initForVendor(
      vendor._id,
      vendor.name,
      vendor.vendorType,
      meetingStart || undefined,
      vendor.logo,
      // Prefill. The menu overrides it via `setCurrency` the moment one loads;
      // for an email-only vendor no menu ever loads and this is the answer.
      vendor.defaultCurrency,
    );

    if (isVendorType(vendor.vendorType, VendorType.EMAIL_ONLY)) {
      this.bookMeetingService.navigateTo(BookMeetingView.EMAIL_REQUEST);
    } else {
      this.bookMeetingService.navigateTo(BookMeetingView.MENU_BROWSE);
    }
  }
}
